import React, { useState } from 'react';
import Card from 'v3/components/Card';
import Text from 'v3/components/Text';
import { useModal } from 'hooks';
import CardHeaderComponent from '../../components/CardHeaderComponent';
import HeaderTotalSummary from '../../components/HeaderTotalSummary';
import {
  AD_VALOREM_PERCENTAGE_PRECISION,
  BRL_PRECISION,
  USD_PRECISION,
  useFreightValues,
} from '../../context';
import { LabelsRow } from '../../components/LabelRows';
import { FreightItemRow } from '../../components/FreightItemRow';
import ExtraFields from '../../components/ExtraFields';
import { SummaryRow } from '../../components/SummaryRow';
import { ExtraFieldButton } from '../../components/ExtraFieldButton';
import ExtraFieldsModal from '../../components/ExtraFieldModal';

export default function InputValuesEdit() {
  const {
    isBRLCurrency,
    dollarToUse,
    customerFields,
    insuranceCoopercargaValue,
    inputsExtraFields,
    setInputsExtraFields,
    data,
    onChangeCoopercargaInsuranceValue,
    setInsuranceCoopercargaValue,
    totalInputValuesComputed,
    shouldUseToll,
    shouldUseDischarge,
    shouldUseGrisAd,
    shouldUseCharge,
    shouldUseFerry,
    inputsTag,
    shouldUseICMS,
    goldenShippingCompany,
  } = useFreightValues();
  const [isOpen, setIsOpen] = useState(false);
  const extraFieldsModal = useModal();

  function onToggleCard() {
    setIsOpen(!isOpen);
  }

  function onRemoveField(index) {
    setInputsExtraFields((old) => old.filter((_, idx) => idx !== index));
  }

  function onChangeCalculatorInsuranceValue(newValue, percentageValue) {
    setInsuranceCoopercargaValue({
      value: newValue.value,
      dollarValue: newValue.dollarValue,
      percentage: percentageValue,
    });
  }
  return (
    <Card containerCardBoxShadow="0px 0px 10px rgba(0, 0, 0, 0.1)">
      <CardHeaderComponent
        label="Descontos e Insumos pré taxa"
        isOpen={isOpen}
        onToggleCard={onToggleCard}
        tooltipContent="Todo valor que precisa descontar antes de tirar a taxa (margem) é alocado neste campo, assim, é chegado no percentual correto para desconto de taxa administrativa (Margem)"
        totalsSection={
          <HeaderTotalSummary
            label="Valor Insumos"
            totalInReal={totalInputValuesComputed.total}
            totalInDollar={totalInputValuesComputed.totalDollarValue}
          />
        }
        hasTagsRow
        tagsContent={inputsTag}
      />
      {isOpen && (
        <>
          <LabelsRow />
          <FreightItemRow
            label="Seguro Coopercarga"
            hasPercentageValue
            percentageValue={insuranceCoopercargaValue?.percentage}
            showPercentageInput
            onChangePercentageValue={(_event, value) => {
              if (data?.cargoValue) {
                const computedValue = (Number(value) / 100) * data?.cargoValue;
                onChangeCoopercargaInsuranceValue(
                  isBRLCurrency
                    ? computedValue.toFixed(BRL_PRECISION)
                    : computedValue.toFixed(USD_PRECISION),
                  value,
                );
              }
            }}
            value={insuranceCoopercargaValue?.value}
            dollarValue={insuranceCoopercargaValue?.dollarValue}
            onChangeValue={(_event, value) => {
              if (data?.cargoValue) {
                const percentageValue = (Number(value) / data?.cargoValue) * 100;
                onChangeCoopercargaInsuranceValue(value, percentageValue);
              }
            }}
            onChangeCalculatorValue={(_event, newValue) => {
              if (data?.cargoValue) {
                const percentageValue = (
                  (Number(newValue?.value) / data?.cargoValue) *
                  100
                ).toFixed(BRL_PRECISION);
                onChangeCalculatorInsuranceValue(newValue, percentageValue);
              }
            }}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            hasTooltip
            tooltipContent="Valor aproximado, pode variar de acordo com o valor do produto na Nota Fiscal"
            showCalculator
            shouldDisableField={!data?.cargoValue}
            id="desconto-seguro-coopercarga"
          />
          {shouldUseToll && (
            <FreightItemRow
              isBRLCurrency={isBRLCurrency}
              dollarToUse={dollarToUse}
              label="Pedágio"
              value={customerFields?.tollValue?.value}
              dollarValue={customerFields?.tollValue?.dollarValue}
              shouldDisableField
              id="desconto-pedagio"
            />
          )}
          {shouldUseDischarge && (
            <FreightItemRow
              isBRLCurrency={isBRLCurrency}
              dollarToUse={dollarToUse}
              label="Descarga"
              value={customerFields?.dischargeValue?.value}
              dollarValue={customerFields?.dischargeValue?.dollarValue}
              shouldDisableField
              id="desconto-descarga"
            />
          )}
          {shouldUseGrisAd && (
            <FreightItemRow
              isBRLCurrency={isBRLCurrency}
              dollarToUse={dollarToUse}
              hasPercentageValue
              label="Griss"
              value={customerFields?.gris?.value}
              dollarValue={customerFields?.gris?.dollarValue}
              percentageValue={customerFields?.gris?.percentage}
              hasTooltip
              tooltipContent="Valor aproximado, pode variar de acordo com o valor do produto na Nota Fiscal"
              shouldDisableField
              id="desconto-gris"
            />
          )}
          {shouldUseGrisAd && (
            <>
              <FreightItemRow
                isBRLCurrency={isBRLCurrency}
                dollarToUse={dollarToUse}
                hasPercentageValue
                label="Ad Valorem"
                decimalDigits={AD_VALOREM_PERCENTAGE_PRECISION}
                value={customerFields?.adValorem?.value}
                dollarValue={customerFields?.adValorem?.dollarValue}
                percentageValue={customerFields?.adValorem?.percentage}
                shouldDisableField
                id="desconto-ad-valorem"
              />
              {goldenShippingCompany && (
                <FreightItemRow
                  isBRLCurrency={isBRLCurrency}
                  dollarToUse={dollarToUse}
                  label="Ad Valorem"
                  fontColor="red"
                  value={customerFields?.adValorem?.value}
                  dollarValue={customerFields?.adValorem?.dollarValue}
                  percentageValue={customerFields?.adValorem?.percentage}
                  shouldDisableField
                  id="desconto-ad-valorem"
                  wasValueRemovedFromTotal
                />
              )}
            </>
          )}
          {shouldUseFerry && (
            <FreightItemRow
              isBRLCurrency={isBRLCurrency}
              dollarToUse={dollarToUse}
              label="Balsa"
              value={customerFields?.ferry?.value}
              dollarValue={customerFields?.ferry?.dollarValue}
              shouldDisableField
              id="desconto-balsa"
            />
          )}
          {shouldUseCharge && (
            <FreightItemRow
              isBRLCurrency={isBRLCurrency}
              dollarToUse={dollarToUse}
              label="Taxa de carregamento"
              value={customerFields?.chargeValue?.value}
              dollarValue={customerFields?.chargeValue?.dollarValue}
              shouldDisableField
              id="desconto-taxa-carregamento"
            />
          )}
          {shouldUseICMS && (
            <FreightItemRow
              isBRLCurrency={isBRLCurrency}
              dollarToUse={dollarToUse}
              hasPercentageValue
              label="ICMS"
              value={customerFields?.ICMS?.value}
              dollarValue={customerFields?.ICMS?.dollarValue}
              percentageValue={customerFields?.ICMS?.percentage}
              shouldDisableField
              id="desconto-icms"
            />
          )}
          {inputsExtraFields?.length > 0 ? (
            <>
              <Text type="regular" weight={500}>
                Valores extras
              </Text>
              <ExtraFields
                fields={inputsExtraFields}
                onChange={setInputsExtraFields}
                onRemoveField={onRemoveField}
              />
            </>
          ) : null}
          <SummaryRow
            label="Valor Insumos"
            totalInReal={totalInputValuesComputed?.total}
            totalInDollar={totalInputValuesComputed?.totalDollarValue}
            id="valor-insumo"
          />
          <ExtraFieldButton
            id="button-abrir-valores-extras-valor-insumos"
            openModal={() => extraFieldsModal.open()}
          />
          <ExtraFieldsModal
            extraFields={inputsExtraFields}
            isOpen={extraFieldsModal.isOpen}
            handleClose={() => extraFieldsModal.close()}
            onSave={(fields) => setInputsExtraFields(fields)}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
          />
        </>
      )}
    </Card>
  );
}
